import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  CirclesGameSession,
  type CirclesSessionStatus,
} from '../../game/circles/circlesSession';
import { saveModePref } from '../../game/modePref';
import { modePath } from '../../game/modeRouting';
import { markTutorialSeen } from '../../game/tutorialFlag';
import { buildTutorialCirclesChart } from '../../game/circles/tutorialCirclesChart';
import '../play.css';
import './circlesTutorial.css';

const TUTORIAL_CHART = buildTutorialCirclesChart();

/**
 * Quick onboarding for Circles mode. Runs a 30-second hand-crafted
 * chart through {@link CirclesGameSession} so the player learns the
 * "click on the circle when the approach ring lines up" rhythm in a
 * controlled environment before being dropped into a real song.
 */
export function CirclesTutorialPage() {
  const navigate = useNavigate();
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const sessionRef = useRef<CirclesGameSession | null>(null);
  const [needsTap, setNeedsTap] = useState(true);
  const [status, setStatus] = useState<CirclesSessionStatus>('idle');
  const isTouch = useMemo(
    () => typeof window !== 'undefined' && window.matchMedia('(pointer: coarse)').matches,
    [],
  );

  useEffect(() => {
    saveModePref('circles');
  }, []);

  useEffect(() => {
    return () => {
      if (sessionRef.current) {
        void sessionRef.current.destroy();
        sessionRef.current = null;
      }
    };
  }, []);

  const startTutorial = useCallback(async () => {
    if (!canvasRef.current) {
      return;
    }
    setNeedsTap(false);
    if (sessionRef.current) {
      await sessionRef.current.destroy();
      sessionRef.current = null;
    }
    const session = new CirclesGameSession({
      chart: TUTORIAL_CHART,
      canvas: canvasRef.current,
      audioOffsetMs: 0,
      onStatusChange: (s) => setStatus(s),
      onComplete: (outcome) => {
        // Only mark the tutorial seen when the player actually
        // completed it. A failed first attempt should re-route them
        // back through the gate next time they open `/circles`.
        if (outcome.completed) {
          markTutorialSeen('circles');
        }
      },
      // Tutorial doesn't bank stars/cups — `enableLoops` stays false so
      // the run finishes naturally on completion instead of looping
      // into infinite-mode repeats.
    });
    sessionRef.current = session;
    try {
      await session.start();
    } catch (err) {
      console.error('Failed to start Circles tutorial:', err);
      setNeedsTap(true);
    }
  }, []);

  useEffect(() => {
    if (!needsTap) {
      return;
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === ' ' || e.code === 'Space' || e.key === 'Enter') {
        e.preventDefault();
        void startTutorial();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [needsTap, startTutorial]);

  const skipTutorial = () => {
    // Mark the Circles tutorial as seen so the player isn't bounced back
    // here every time they open `/circles` after explicitly opting out.
    markTutorialSeen('circles');
    navigate(modePath('circles'));
  };

  return (
    <div className="play-page tutorial-page" data-testid="circles-tutorial-page">
      <div className="play-canvas-wrap">
        <canvas
          ref={canvasRef}
          className="play-canvas"
          data-testid="circles-tutorial-canvas"
        />

        {needsTap && (
          <div className="play-overlay" data-testid="circles-tutorial-start-overlay">
            <div className="play-overlay-card">
              <h2>Welcome to Circles</h2>
              <p>
                {isTouch ? 'Tap the circles' : 'Click each circle'} as the white
                approach ring shrinks down to meet it.
              </p>
              <ul className="circles-tutorial-rules">
                <li>
                  <strong>Perfect window:</strong> tap exactly when the rings
                  meet (±50ms).
                </li>
                <li>
                  <strong>Good window:</strong> a little before/after still
                  scores partial credit (±120ms).
                </li>
                <li>
                  <strong>Spinners</strong>: spin the cursor around the centre —
                  the faster you spin, the more points you bank.
                </li>
                <li>
                  <strong>Off-circle clicks</strong> are free during quiet
                  stretches but cost HP near a real circle.
                </li>
              </ul>
              <p className="muted faint">
                30-second tutorial &middot; headphones recommended.
              </p>
              <div className="play-overlay-actions">
                <button
                  type="button"
                  className="btn btn-primary btn-large"
                  onClick={() => void startTutorial()}
                  data-testid="circles-tutorial-start"
                >
                  Start tutorial
                </button>
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={skipTutorial}
                  data-testid="circles-tutorial-skip"
                >
                  Skip — take me to the songs
                </button>
              </div>
            </div>
          </div>
        )}

        {(status === 'completed' || status === 'failed') && (
          <div className="play-overlay" data-testid="circles-tutorial-complete-overlay">
            <div className="play-overlay-card">
              <h2>{status === 'completed' ? "You're ready" : 'Try again?'}</h2>
              <p>
                {status === 'completed'
                  ? 'Pick a real track to start collecting stars.'
                  : 'Take another shot — the real songs ramp up gradually from On & On.'}
              </p>
              <div className="play-overlay-actions">
                <Link
                  to={modePath('circles')}
                  className="btn btn-primary btn-large"
                >
                  Browse tracks
                </Link>
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={() => {
                    setNeedsTap(true);
                    setStatus('idle');
                  }}
                >
                  Replay tutorial
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
